import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { getConference, updateConference, updateChecklistItem, addChecklistItem, deleteChecklistItem } from '@/services/conferencesService';

const BLUE = '#002169';
const ACCENT = '#FFAF3C';

const STATUS_STYLES = {
  planning:  { bg: 'rgba(255,255,255,0.15)', color: '#fff', label: 'Planning' },
  confirmed: { bg: 'rgba(255,255,255,0.15)', color: '#fff', label: 'Confirmed' },
  done:      { bg: 'rgba(255,255,255,0.15)', color: '#fff', label: 'Done' },
  cancelled: { bg: 'rgba(255,255,255,0.15)', color: '#fff', label: 'Cancelled' },
};

const CHECKLIST_SECTIONS = [
  { label: 'Registration & Payments', keys: ['eoi_submitted','registration','deposit','full_payment'] },
  { label: 'Travel & Accommodation',  keys: ['flights','accommodation'] },
  { label: 'Logistics',               keys: ['courier','bump_in','bump_out','marketing_materials','logistics_notes'] },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

const ConferenceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingStatus, setEditingStatus]     = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationValue, setLocationValue]     = useState('');
  const [savingLocation, setSavingLocation]   = useState(false);
  const [editingDates, setEditingDates]       = useState(false);
  const [startDateValue, setStartDateValue]   = useState('');
  const [endDateValue, setEndDateValue]       = useState('');
  const [savingDates, setSavingDates]         = useState(false);
  const [expandedNotes, setExpandedNotes]     = useState({}); // itemId → bool
  const [newItemLabel, setNewItemLabel]       = useState('');
  const [addingItem, setAddingItem]           = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getConference(id);
      setData(res);
      setNotesValue(res?.notes || '');
      setLocationValue(res?.location || '');
      setStartDateValue(res?.start_date ? res.start_date.slice(0, 10) : '');
      setEndDateValue(res?.end_date ? res.end_date.slice(0, 10) : '');
    } catch { setError('Failed to load conference'); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Toggle checklist item
  const handleToggleItem = async (item) => {
    const newVal = !item.is_completed;
    // Optimistic
    setData(prev => ({
      ...prev,
      checklist: prev.checklist.map(i => i.id === item.id
        ? { ...i, is_completed: newVal ? 1 : 0, completed_at: newVal ? new Date().toISOString() : null }
        : i)
    }));
    try {
      await updateChecklistItem(id, item.id, { is_completed: newVal });
    } catch {
      // Revert on failure
      setData(prev => ({
        ...prev,
        checklist: prev.checklist.map(i => i.id === item.id ? { ...i, is_completed: item.is_completed } : i)
      }));
    }
  };

  // Update item field (due_date or notes)
  const handleUpdateItemField = async (item, field, value) => {
    setData(prev => ({
      ...prev,
      checklist: prev.checklist.map(i => i.id === item.id ? { ...i, [field]: value } : i)
    }));
    try {
      await updateChecklistItem(id, item.id, { [field]: value });
    } catch (e) { console.error(e); }
  };

  // Save conference notes
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateConference(id, { notes: notesValue });
      setData(prev => ({ ...prev, notes: notesValue }));
      setEditingNotes(false);
    } catch (e) { console.error(e); }
    setSavingNotes(false);
  };

  // Save conference status
  const handleStatusChange = async (newStatus) => {
    try {
      await updateConference(id, { status: newStatus });
      setData(prev => ({ ...prev, status: newStatus }));
    } catch (e) { console.error(e); }
    setEditingStatus(false);
  };

  // Save location
  const handleSaveLocation = async () => {
    setSavingLocation(true);
    try {
      await updateConference(id, { location: locationValue });
      setData(prev => ({ ...prev, location: locationValue }));
      setEditingLocation(false);
    } catch (e) { console.error(e); }
    setSavingLocation(false);
  };

  // Save dates
  const handleSaveDates = async () => {
    setSavingDates(true);
    try {
      await updateConference(id, { start_date: startDateValue, end_date: endDateValue });
      setData(prev => ({ ...prev, start_date: startDateValue, end_date: endDateValue }));
      setEditingDates(false);
    } catch (e) { console.error(e); }
    setSavingDates(false);
  };

  // Add ad-hoc checklist item
  const handleAddItem = async () => {
    if (!newItemLabel.trim()) return;
    setAddingItem(true);
    try {
      const res = await addChecklistItem(id, newItemLabel.trim());
      setData(prev => ({ ...prev, checklist: [...(prev.checklist || []), res.item] }));
      setNewItemLabel('');
    } catch (e) { console.error(e); }
    setAddingItem(false);
  };

  // Delete an ad-hoc item
  const handleDeleteItem = async (item) => {
    setData(prev => ({ ...prev, checklist: prev.checklist.filter(i => i.id !== item.id) }));
    try {
      await deleteChecklistItem(id, item.id);
    } catch (e) {
      // Revert on failure
      setData(prev => ({ ...prev, checklist: [...prev.checklist, item] }));
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><CircularProgress style={{ color: BLUE }} /></div>;
  if (error || !data) return <div className="flex justify-center items-center min-h-screen text-red-500">{error || 'Conference not found'}</div>;

  const checklist = data.checklist || [];
  const totalItems = checklist.length;
  const doneItems  = checklist.filter(i => i.is_completed).length;
  const pct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  const itemsByKey = new Map(checklist.map(i => [i.item_key, i]));

  return (
    <div className="bg-gray-50 min-w-0 max-w-full">
      {/* Header */}
      <div style={{ background: BLUE }} className="px-6 py-5">
        <button onClick={() => navigate('/admin-functions/conferences')} className="flex items-center gap-1 text-sm mb-3 transition" style={{ color: 'rgba(255,255,255,0.6)' }}>
          <ArrowBackIosIcon style={{ fontSize: 13 }} /> All Conferences
        </button>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-extrabold text-white truncate">{data.name}</h1>
            {editingDates ? (
              <div className="flex items-center gap-2 mt-1">
                <input type="date" value={startDateValue} onChange={e => setStartDateValue(e.target.value)}
                  className="text-xs rounded-md px-2 py-1 focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                />
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>–</span>
                <input type="date" value={endDateValue} onChange={e => setEndDateValue(e.target.value)}
                  className="text-xs rounded-md px-2 py-1 focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                />
                <button onClick={handleSaveDates} disabled={savingDates} className="p-1 rounded transition" style={{ background: ACCENT, color: BLUE }}>
                  <CheckIcon style={{ fontSize: 14 }} />
                </button>
                <button onClick={() => { setEditingDates(false); setStartDateValue(data.start_date?.slice(0,10) || ''); setEndDateValue(data.end_date?.slice(0,10) || ''); }}
                  className="p-1 rounded transition" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            ) : (
              <button onClick={() => setEditingDates(true)} className="flex items-center gap-1 mt-0.5 group">
                <p style={{ color: 'rgba(255,255,255,0.7)' }} className="text-sm">
                  {fmtDate(data.start_date)} – {fmtDate(data.end_date)}
                </p>
                <EditIcon style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }} className="group-hover:opacity-100 opacity-0 transition" />
              </button>
            )}
            {/* Location inline edit */}
            {editingLocation ? (
              <div className="flex items-center gap-2 mt-1.5">
                <PlaceOutlinedIcon style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
                <input autoFocus value={locationValue} onChange={e => setLocationValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveLocation(); if (e.key === 'Escape') setEditingLocation(false); }}
                  placeholder="Location…"
                  className="flex-1 text-xs rounded-md px-2 py-1 focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', minWidth: 180 }}
                />
                <button onClick={handleSaveLocation} disabled={savingLocation} className="p-1 rounded transition" style={{ background: ACCENT, color: BLUE }}>
                  <CheckIcon style={{ fontSize: 14 }} />
                </button>
                <button onClick={() => { setEditingLocation(false); setLocationValue(data.location || ''); }} className="p-1 rounded transition" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            ) : (
              <button onClick={() => setEditingLocation(true)} className="flex items-center gap-1 mt-1.5 group">
                <PlaceOutlinedIcon style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
                <span className="text-xs" style={{ color: data.location ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.35)' }}>
                  {data.location || 'Add location…'}
                </span>
                <EditIcon style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }} className="group-hover:opacity-100 opacity-0 transition" />
              </button>
            )}
          </div>
          {/* Status badge / edit */}
          <div className="flex-shrink-0">
            {editingStatus ? (
              <select autoFocus defaultValue={data.status} onChange={e => handleStatusChange(e.target.value)} onBlur={() => setEditingStatus(false)}
                className="text-xs font-semibold px-2 py-1 rounded-lg border-0 focus:outline-none bg-white text-gray-800">
                {['planning','confirmed','done','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            ) : (
              <button onClick={() => setEditingStatus(true)}
                className="text-xs font-bold px-3 py-1 rounded-full cursor-pointer transition hover:opacity-80"
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                {data.status.charAt(0).toUpperCase()+data.status.slice(1)} ▾
              </button>
            )}
          </div>
        </div>
        {/* Progress */}
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ACCENT }} />
          </div>
          <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'rgba(255,255,255,0.75)' }}>{doneItems} of {totalItems} complete</span>
        </div>
        {/* Notes inline edit */}
        <div className="mt-3">
          {editingNotes ? (
            <div className="flex items-start gap-2">
              <textarea
                value={notesValue} onChange={e => setNotesValue(e.target.value)} rows={2} autoFocus
                className="flex-1 text-xs rounded-lg px-2 py-1.5 focus:outline-none resize-none"
                style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}
                placeholder="Conference notes…"
              />
              <button onClick={handleSaveNotes} disabled={savingNotes}
                className="p-1.5 rounded-lg transition" style={{ background: ACCENT, color: BLUE }}>
                <CheckIcon style={{ fontSize: 16 }} />
              </button>
              <button onClick={() => { setEditingNotes(false); setNotesValue(data.notes || ''); }}
                className="p-1.5 rounded-lg transition" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}>
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-2 cursor-pointer group" onClick={() => setEditingNotes(true)}>
              <span className="text-xs flex-1" style={{ color: 'rgba(255,255,255,0.65)', fontStyle: data.notes ? 'italic' : 'normal' }}>
                {data.notes || '+ Add conference notes…'}
              </span>
              <EditIcon style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)' }} className="group-hover:opacity-100 opacity-0 transition flex-shrink-0 mt-0.5" />
            </div>
          )}
        </div>
      </div>

      {/* Checklist — full width, two column grid */}
      <div className="px-4 py-4 min-w-0 max-w-full">
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {CHECKLIST_SECTIONS.map((section, si) => {
            const sectionItems = section.keys.map(k => itemsByKey.get(k)).filter(Boolean);
            if (!sectionItems.length) return null;
            return (
              <div key={section.label} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{section.label}</span>
                </div>
                {sectionItems.map((item, idx) => {
                  const isLast = idx === sectionItems.length - 1;
                  const expanded = !!expandedNotes[item.id] || !!item.notes;
                  return (
                    <div key={item.id} className={`px-5 py-3.5 ${!isLast ? 'border-b border-gray-50' : ''}`}>
                      <div className="flex items-start gap-3">
                        {/* no change to template items */}
                        {/* Checkbox */}
                        <button
                          onClick={() => handleToggleItem(item)}
                          className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition mt-0.5"
                          style={{
                            background: item.is_completed ? '#10b981' : 'transparent',
                            border: item.is_completed ? '2px solid #10b981' : '2px solid #d1d5db',
                          }}
                        >
                          {item.is_completed && <CheckIcon style={{ fontSize: 12, color: '#fff' }} />}
                        </button>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: item.is_completed ? '#9ca3af' : '#111827' }}>
                              {item.label}
                            </span>
                          </div>

                          {/* Due/event date — for registration, deposit, full_payment, bump_in, bump_out */}
                          {item.supports_due && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-xs text-gray-400">{item.due_label || 'Due'}:</span>
                              <input type="date" value={item.due_date ? item.due_date.slice(0,10) : ''}
                                onChange={e => handleUpdateItemField(item, 'due_date', e.target.value || null)}
                                className="text-xs border border-gray-200 rounded-md px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-300"
                              />
                            </div>
                          )}

                          {/* Notes — show textarea if expanded or has existing notes */}
                          {item.item_key === 'logistics_notes' ? (
                            <textarea value={item.notes || ''} rows={3}
                              onChange={e => handleUpdateItemField(item, 'notes', e.target.value)}
                              placeholder="Add logistics notes…"
                              className="mt-2 w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-300 resize-none bg-amber-50"
                            />
                          ) : (
                            <>
                              {!expanded && !item.notes && (
                                <button onClick={() => setExpandedNotes(p => ({ ...p, [item.id]: true }))}
                                  className="mt-1 text-xs text-gray-400 hover:text-gray-600 transition">
                                  + add notes
                                </button>
                              )}
                              {(expanded || item.notes) && (
                                <textarea value={item.notes || ''} rows={2}
                                  onChange={e => handleUpdateItemField(item, 'notes', e.target.value)}
                                  placeholder="Notes…"
                                  className="mt-1.5 w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-300 resize-none"
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Ad-hoc items card */}
          {(() => {
            const adhocItems = checklist.filter(i => i.item_key === 'adhoc');
            return (
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-400">Custom Items</span>
                </div>

                {adhocItems.length === 0 && (
                  <div className="px-5 py-3 text-xs text-gray-400 italic">No custom items yet.</div>
                )}

                {adhocItems.map((item, idx) => {
                  const isLast = idx === adhocItems.length - 1;
                  const expanded = !!expandedNotes[item.id] || !!item.notes;
                  return (
                    <div key={item.id} className={`px-5 py-3.5 ${!isLast ? 'border-b border-gray-50' : ''}`}>
                      <div className="flex items-start gap-3">
                        <button onClick={() => handleToggleItem(item)}
                          className="flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition mt-0.5"
                          style={{ background: item.is_completed ? '#10b981' : 'transparent', border: item.is_completed ? '2px solid #10b981' : '2px solid #d1d5db' }}>
                          {item.is_completed && <CheckIcon style={{ fontSize: 12, color: '#fff' }} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: item.is_completed ? '#9ca3af' : '#111827' }}>
                              {item.label}
                            </span>
                          </div>
                          {!expanded && !item.notes && (
                            <button onClick={() => setExpandedNotes(p => ({ ...p, [item.id]: true }))} className="mt-1 text-xs text-gray-400 hover:text-gray-600 transition">+ add notes</button>
                          )}
                          {(expanded || item.notes) && (
                            <textarea value={item.notes || ''} rows={2}
                              onChange={e => handleUpdateItemField(item, 'notes', e.target.value)}
                              placeholder="Notes…"
                              className="mt-1.5 w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-300 resize-none"
                            />
                          )}
                        </div>
                        {/* Delete button — only on ad-hoc items */}
                        <button onClick={() => handleDeleteItem(item)} title="Remove item"
                          className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-red-400 transition mt-0.5">
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add new ad-hoc item */}
                <div className="px-5 py-3 border-t border-gray-50">
                  <div className="flex items-center gap-2">
                    <input
                      value={newItemLabel}
                      onChange={e => setNewItemLabel(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddItem()}
                      placeholder="Add a custom checklist item…"
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-300"
                    />
                    <button onClick={handleAddItem} disabled={addingItem || !newItemLabel.trim()}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-40 transition"
                      style={{ background: BLUE }}>
                      <AddIcon style={{ fontSize: 14 }} />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ConferenceDetailPage;
